
  import { defineComponent, ref, onMounted, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import moment from 'moment';

  export default defineComponent({
    name: 'expert-review-reject-reasons-listing',
    components: {
      Datatable,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(
          translate('expertReviewRejectReasonsListing'),
          [translate('indices')]
        );
      });

      await store.dispatch(Actions.GET_EXPERT_REVIEW_REJECT_REASONS_LIST);
      const loading = ref(false);

      const tableHeader = ref([
        {
          name: translate('idColumn'),
          key: 'id',
          sortable: true,
        },
        {
          name: translate('rejectReasonEn'),
          key: 'name_en',
          sortable: true,
        },
        {
          name: translate('rejectReasonAr'),
          key: 'name_ar',
          sortable: true,
        },
        {
          name: translate('slug'),
          key: 'slug',
          sortable: true,
        },
        {
          name: translate('status'),
          key: 'is_active',
          sortable: true,
        },
        {
          name: translate('createdAt'),
          key: 'created_at',
          sortable: true,
        },
        {
          name: translate('lastUpdated'),
          key: 'updated_at',
          sortable: true,
        },
        {
          name: translate('actions'),
          key: 'actions',
        },
      ]);

      const formatDate = (date) => moment(date).format('YYYY-MM-DD HH:mm');

      const canToggle = (item) =>
        item.slug !== 'other' &&
        (item.is_active ? can('remove', 'indices') : can('update', 'indices'));

      const toggleStatus = async (item) => {
        if (!canToggle(item)) return;

        const confirmMessage = item.is_active
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(confirmMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          if (item.is_active) {
            await store.dispatch(
              Actions.DELETE_EXPERT_REVIEW_REJECT_REASON,
              item.id
            );
          } else {
            await store.dispatch(Actions.UPDATE_EXPERT_REVIEW_REJECT_REASON, {
              id: item.id,
              data: { isActive: true },
            });
          }
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = errorName ? store.getters.getErrors[errorName] : null;

          await store.dispatch(Actions.GET_EXPERT_REVIEW_REJECT_REASONS_LIST);
          loading.value = false;
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = item.is_active
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      const editItem = (id) => {
        router.push({
          name: 'expert-review-reject-reasons-editing',
          params: { id },
        });
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(
          Actions.UPDATE_EXPERT_REVIEW_REJECT_REASON_CURRENT_PAGE,
          val
        );
        await store.dispatch(Actions.GET_EXPERT_REVIEW_REJECT_REASONS_LIST);
        loading.value = false;
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        tableData: computed(() => store.getters.expertReviewRejectReasonsList),
        tableHeader,
        canToggle,
        toggleStatus,
        translate,
        totalItems: computed(
          () => store.getters.expertReviewRejectReasonsListCount
        ),
        pageChanged,
        goTo,
        editItem,
        formatDate,
        loading,
        can,
      };
    },
  });
